import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import type {
  CreateGroupRequest,
  LeaderboardPeriod,
  UpdateGroupRequest,
} from "@/lib/types";

export function useMyGroups() {
  return useQuery({
    queryKey: queryKeys.groups.mine(),
    queryFn: api.getMyGroups,
    staleTime: 30 * 1000,
  });
}

export function useAdminGroups() {
  return useQuery({
    queryKey: queryKeys.groups.admin(),
    queryFn: api.getAdminGroups,
    staleTime: 30 * 1000,
  });
}

export function useGroup(gid: string) {
  return useQuery({
    queryKey: queryKeys.groups.detail(gid),
    queryFn: () => api.getGroup(gid),
    enabled: !!gid,
    staleTime: 30 * 1000,
  });
}

export function useGroupMembers(gid: string) {
  return useQuery({
    queryKey: queryKeys.groups.members(gid),
    queryFn: () => api.getGroupMembers(gid),
    enabled: !!gid,
    staleTime: 30 * 1000,
  });
}

export function useGroupLeaderboard(
  gid: string,
  opts: {
    period?: LeaderboardPeriod;
    from?: string;
    to?: string;
    exam?: string;
  } = {},
) {
  return useQuery({
    queryKey: queryKeys.groups.leaderboard(gid, opts),
    queryFn: () => api.getGroupLeaderboard(gid, opts),
    enabled: !!gid,
    // Aggregation hits BatchGet for every member × exam × day; keep results
    // warm for a couple of minutes so quick tab toggles don't re-fetch.
    staleTime: 2 * 60 * 1000,
  });
}

export function useMyInvitations() {
  return useQuery({
    queryKey: queryKeys.invitations.mine(),
    queryFn: api.getMyInvitations,
    // Drives the navbar badge — refresh on focus so users see new invites
    // without having to navigate.
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGroupRequest) => api.createGroup(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.admin() });
      qc.invalidateQueries({ queryKey: queryKeys.groups.mine() });
    },
  });
}

export function useUpdateGroup(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateGroupRequest) => api.updateGroup(gid, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.detail(gid) });
      qc.invalidateQueries({ queryKey: queryKeys.groups.admin() });
      qc.invalidateQueries({ queryKey: queryKeys.groups.mine() });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gid: string) => api.deleteGroup(gid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.all });
    },
  });
}

export function useSendGroupInvites(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emails: string[]) => api.sendGroupInvites(gid, emails),
    onSuccess: () => {
      // Member count may change once invitees accept, but the invite call
      // itself doesn't move the count — no need to invalidate members here.
      qc.invalidateQueries({ queryKey: queryKeys.groups.detail(gid) });
    },
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gid: string) => api.acceptInvitation(gid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invitations.mine() });
      qc.invalidateQueries({ queryKey: queryKeys.groups.mine() });
      // Server now grants exam access on accept (v1 비대칭 정책: 추가만
      // propagate). Refresh the user's exam list so newly granted exams
      // appear immediately without a manual reload.
      qc.invalidateQueries({ queryKey: queryKeys.exams.mine() });
    },
  });
}

export function useRejectInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gid: string) => api.rejectInvitation(gid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invitations.mine() });
    },
  });
}

export function useRemoveGroupMember(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => api.removeGroupMember(gid, uid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.members(gid) });
      qc.invalidateQueries({ queryKey: queryKeys.groups.detail(gid) });
    },
  });
}

export function useChangeMemberRole(gid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: "admin" | "member" }) =>
      api.changeMemberRole(gid, uid, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.members(gid) });
    },
  });
}
